import { X } from 'lucide-react';
import type { Retencion } from './contrato';

/** Qué impuesto se retuvo, dicho como lo nombra el SRI. */
const IMPUESTOS: Record<string, string> = { '1': 'Renta', '2': 'IVA' };

/**
 * El detalle de una retención: concepto a concepto, con la base y el porcentaje.
 *
 * <p>Es lo que hay que poder enseñar cuando el proveedor reclama: de qué factura salió, sobre
 * qué base se calculó y con qué porcentaje. El total solo no responde ninguna de esas tres.
 */
export function PanelDeLaRetencion({
  retencion: r,
  alCerrar,
}: {
  retencion: Retencion;
  alCerrar: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-armazon-950/50">
      <aside className="h-full w-full max-w-lg overflow-y-auto bg-white shadow-2xl">
        <header className="flex items-start justify-between gap-3 border-b border-armazon-200 px-5 py-4">
          <div className="min-w-0">
            <p className="importe text-sm font-bold text-armazon-800">
              {r.numero ?? 'Sin emitir'}
            </p>
            <p className="mt-0.5 text-[11px] uppercase tracking-wide text-armazon-500">
              Período {r.periodoFiscal}
            </p>
          </div>
          <button
            type="button"
            onClick={alCerrar}
            aria-label="Cerrar"
            className="rounded-lg p-1.5 text-armazon-400 transition hover:bg-armazon-100"
          >
            <X className="size-4" aria-hidden />
          </button>
        </header>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 px-5 py-4 text-[11px] uppercase tracking-wide">
          <Dato rotulo="Proveedor" valor={r.proveedor} ancho />
          <Dato rotulo="Identificación" valor={r.identificacion} />
          <Dato rotulo="Retiene de" valor={r.comprobanteNumero} />
          <Dato rotulo="Emitida" valor={enFecha(r.fechaEmision)} />
          <Dato rotulo="Total retenido" valor={`$ ${r.totalRetenido.toFixed(2)}`} />
          {r.claveAcceso && <Dato rotulo="Clave de acceso" valor={r.claveAcceso} ancho />}
          {r.numeroAutorizacion && (
            <Dato rotulo="Autorización" valor={r.numeroAutorizacion} ancho />
          )}
        </dl>

        {r.mensajeSri && (
          <p className="mx-5 mb-4 rounded-xl bg-armazon-50 p-3 text-[11px] uppercase leading-relaxed tracking-wide text-armazon-600">
            {r.mensajeSri}
          </p>
        )}

        {r.motivoAnulacion && (
          <p className="mx-5 mb-4 rounded-xl bg-red-50 p-3 text-[11px] uppercase leading-relaxed tracking-wide text-alerta">
            Anulada: {r.motivoAnulacion}
          </p>
        )}

        <h3 className="border-y border-armazon-200 bg-armazon-50/70 px-5 py-2 text-[11px] font-semibold uppercase tracking-wide text-armazon-600">
          Lo retenido
        </h3>

        <ul className="divide-y divide-armazon-100">
          {r.detalles.map((d) => (
            <li key={d.id} className="px-5 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-armazon-800">
                    {IMPUESTOS[d.impuesto] ?? d.impuesto} · {d.codigoRetencion}
                  </p>
                  <p className="mt-0.5 text-[11px] uppercase leading-relaxed tracking-wide text-armazon-500">
                    {d.descripcion}
                  </p>
                </div>
                <p className="importe shrink-0 text-sm font-bold text-armazon-900">
                  ${d.valorRetenido.toFixed(2)}
                </p>
              </div>
              <p className="mt-1 text-[10px] uppercase tracking-wide text-armazon-400">
                Base ${d.baseImponible.toFixed(2)} × {d.porcentaje}%
              </p>
            </li>
          ))}
        </ul>
      </aside>
    </div>
  );
}

function Dato({ rotulo, valor, ancho = false }: { rotulo: string; valor: string; ancho?: boolean }) {
  return (
    <div className={ancho ? 'col-span-2' : ''}>
      <dt className="text-armazon-400">{rotulo}</dt>
      <dd className="importe mt-0.5 break-words font-semibold text-armazon-800">{valor}</dd>
    </div>
  );
}

function enFecha(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${anio}`;
}
